<template>
  <div v-click-outside="(event) => $emit('click-outside', event)" class="notifications-panel">
    <transition name="simple-fade-in-out">
      <div v-if="isOpen" class="notifications-panel__inner-wrap">
        <!-- Panel header -->
        <header
          :class="{
            'panel-header': true,
            'notifications-panel__panel-header': true,
            'notifications-panel__panel-header--bigger-bottom-margin': $store.notifications.isLoading,
          }"
        >
          <div class="panel-header__title">Notifications</div>

          <!-- Caret icon button -->
          <div
            :class="{
              'caret-icon-button': true,
              'panel-header__caret-icon-button': true,
            }"
            @click="$emit('close-icon-click')"
            v-html="icons.singles.arrowRightIcon"
          ></div>
          <!-- / Caret icon button -->

          <div
            v-if="$store.notifications.loaded && $store.notifications.loaded.length"
            :class="{
              'dismiss-all-button': true,
              'panel-header__dismiss-all-button': true,
            }"
            @click="$store.notifications.dismissAll()"
          >
            <div class="dismiss-all-button__icon" v-html="icons.singles.cancelIcon"></div>
            Dismiss all
          </div>
        </header>
        <!-- / Header -->

        <!-- List skeleton -->
        <ul
          v-if="$store.notifications.isLoading"
          :class="{
            'list-skeleton': true,
            'notifications-panel__list-skeleton': true,
          }"
        >
          <!-- Notification skeleton -->
          <li v-for="notification in 8" :key="notification" class="notification-skeleton list-skeleton__notification">
            <div class="notification-skeleton__date-n-close-button">
              <div class="notification-skeleton__date"></div>
              <div class="notification-skeleton__close-button"></div>
            </div>
            <div class="notification-skeleton__icon"></div>
            <div class="notification-skeleton__title-n-text">
              <div class="notification-skeleton__title"></div>
              <div class="notification-skeleton__text"></div>
            </div>
          </li>
          <!-- / Notification skeleton -->
        </ul>
        <!-- / List skeleton -->

        <!-- List placeholder -->
        <transition name="simple-fade-in-out">
          <div
            v-if="$store.notifications.loaded && $store.notifications.loaded.length === 0"
            style="transition-delay: 600ms"
            :class="{
              'list-placeholder': true,
              'notifications-panel__list-placeholder': true,
            }"
          >
            <div class="list-placeholder__icon" v-html="icons.singles.bellIcon"></div>
            You have no notifications
          </div>
        </transition>
        <!-- / List placeholder -->

        <div
          v-if="$store.notifications.loaded && $store.notifications.loaded.length"
          class="notifications-panel__scrollable-block-wrapper"
        >
          <!-- scrollable block -->
          <scrollable-block class="notifications-panel__scrollable-block" shadows-color="white">
            <ul class="notifications-panel__sections-list">
              <!-- Notifications section -->
              <li
                v-if="$store.notifications.unreadNotifications.length"
                :class="{
                  'notifications-section': true,
                  'notifications-panel__notifications-section': true,
                  'notifications-panel__notifications-section--new-ones': true,
                }"
              >
                <div
                  v-for="(item, index) in $store.notifications.unreadNotifications"
                  :key="item.id"
                  class="notifications-section__snippets-list"
                >
                  <notification-item
                    :class="{
                      'notifications-section__snippet': true,
                      'notifications-section__snippet--new-one': true,
                      'notifications-section__snippet--dismissed': item.id === dismissedNotificationId,
                    }"
                    :notification="item"
                    :is-last="index === $store.notifications.unreadNotifications.length - 1"
                    @dismiss="dismissCertainNotification(item.id)"
                  />
                </div>
              </li>
              <!-- / Notifications section -->

              <!-- Notifications section -->
              <li
                v-if="$store.notifications.readNotifications.length"
                :class="{
                  'notifications-section': true,
                  'notifications-panel__notifications-section': true,
                  'notifications-panel__notifications-section--old-ones': true,
                }"
              >
                <div v-if="$store.notifications.unreadNotifications.length" class="notifications-section__title">
                  Read Notifications
                </div>

                <div
                  v-for="item in $store.notifications.readNotifications"
                  :key="item.id"
                  class="notifications-section__snippets-list"
                >
                  <notification-item
                    :class="{
                      'notifications-section__snippet': true,
                      'notifications-section__snippet--dismissed': item.id === dismissedNotificationId,
                    }"
                    :notification="item"
                    @dismiss="dismissCertainNotification(item.id)"
                  />
                </div>
              </li>
              <!-- / Notifications section -->
            </ul>
          </scrollable-block>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import vClickOutside from 'click-outside-vue3';

// Components
import NotificationItem from './notification-item.vue';
import ScrollableBlock from '@components/scrollable-block.vue';

// Icons
// singles
import arrowRightIcon from '@icons/other/arrow-right.svg';
import bellIcon from '@icons/other2/bell.svg';
import { useIntercomOutOfTheWay } from '@lib/intercom';
import cancelIcon from '@icons/other/cancel.svg';
import icoCrossIcon from '@icons/other/ico-cross.svg';

export default {
  name: 'notifications-panel',

  props: {
    isOpen: { type: Boolean, required: true, default: false },
  },

  setup(props) {
    // The Intercom bubble is fixed over the bottom-right, which is where this panel lands.
    useIntercomOutOfTheWay(() => props.isOpen);
  },

  directives: {
    clickOutside: vClickOutside?.directive,
  },

  components: {
    NotificationItem,
    ScrollableBlock,
  },

  data() {
    return {
      dismissedNotificationId: null,

      icons: {
        singles: {
          arrowRightIcon,
          bellIcon,
          cancelIcon,
          icoCrossIcon,
        },
      },
    };
  },

  methods: {
    dismissCertainNotification(notificationId) {
      this.dismissedNotificationId = notificationId;

      setTimeout(() => {
        this.$store.notifications.dismiss(notificationId);
        this.dismissedNotificationId = null;
      }, 600);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';
@import '@styles/media-queries.scss';
@import '@styles/mixins.scss';

// Notification skeleton ------------------------------------------------------
.notification-skeleton {
  padding-left: 3.2rem;
  display: flex;
  position: relative;

  &__date-n-close-button {
    display: flex;
    position: absolute;
    inset: 0 2.3rem auto auto;
  }

  &__date {
    @include skeletonPlaceholder;
    width: 4.3rem;
    height: 1.2rem;
    margin-right: 1.5rem;
    border-radius: 10rem;
  }

  &__close-button {
    @include skeletonPlaceholder;
    width: 1.2rem;
    height: 1.2rem;
    border-radius: 10rem;
  }

  &__icon {
    @include skeletonPlaceholder;
    width: 2.7rem;
    height: 2.7rem;
    margin-right: 1.8rem;
    border-radius: 10rem;
  }

  &__title-n-text {
  }

  &__title {
    @include skeletonPlaceholder;
    width: 14.7rem;
    height: 1.9rem;
    margin-bottom: 0.8rem;
    border-radius: 10rem;
  }

  &__text {
    @include skeletonPlaceholder;
    width: 29.8rem;
    height: 1.7rem;
    border-radius: 10rem;
  }
}

// List skeleton --------------------------------------------------------------
.list-skeleton {
  padding: 0;
  margin: 0;
  list-style: none;

  &__notification {
    margin-bottom: 3.8rem;
  }
}

// Notifications section ------------------------------------------------------
.notifications-section {
  &__title {
    padding-left: 3.2rem;
    font-size: 1.2rem;
    text-transform: uppercase;
    color: rgba($c-dark, 0.5);
  }

  &__snippets-list-wrap {
  }

  &__snippets-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__snippet {
    max-height: 9999rem;
    transition:
      transform 0.3s ease-in-out,
      max-height 0.3s cubic-bezier(0, 1, 0, 1);
    transition-delay: 0s, 0.3s;

    &--dismissed {
      max-height: 0 !important;
      transform: translateX(100%);
    }
  }
}

// Dismiss all button ---------------------------------------------------------
.dismiss-all-button {
  display: flex;
  align-items: center;
  position: relative;
  font-size: 1.2rem;
  line-height: 1.2rem;
  transition: opacity 0.1s ease;
  cursor: pointer;

  &::before {
    content: '';
    width: calc(100% + 2.6rem);
    height: calc(100% + 2.2rem);
    position: absolute;
    inset: 50% auto auto 50%;
    transform: translate(-50%, -50%);
  }

  &__icon {
    width: 1.3rem;
    height: 1.3rem;
    margin-right: 0.7rem;

    :deep(svg) {
      fill: $c-dark;
    }
  }

  &:hover {
    opacity: 0.5;
  }
}

// Caret icon button ----------------------------------------------------------
.caret-icon-button {
  width: 2.4rem;
  height: 2.4rem;
  box-sizing: border-box;
  border: 2px rgba($c-dark, 0.5) solid;
  border-radius: 10rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: #fff;
  transition: border-color 0.15s ease;
  cursor: pointer;
  user-select: none;

  &::before {
    content: '';
    width: calc(100% + 2rem);
    height: calc(100% + 2rem);
    position: absolute;
    inset: 50% auto auto 50%;
    z-index: -1;
    transform: translate(-50%, -50%);
  }

  &:hover {
    border-color: rgba($c-dark, 1);
  }

  :deep(svg) {
    width: calc(100% - 0.8rem);
    height: calc(100% - 0.8rem);
    position: relative;
    right: -1px;
    fill: rgba($c-dark, 0.5);
    transition: fill 0.15s ease;
  }

  &:hover :deep(svg) {
    fill: rgba($c-dark, 1);
  }
}

// Panel header ---------------------------------------------------------------
.panel-header {
  padding: 0 2.4rem 0 3.2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__title {
    font:
      2rem/2rem 'Montserrat',
      sans-serif;
  }

  &__caret-icon-button {
    position: absolute;
    inset: 2rem auto auto -1.2rem;
    z-index: 2;
  }

  &__dismiss-all-button {
  }
}

// List placeholder -----------------------------------------------------------
.list-placeholder {
  height: 42rem;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgba($c-dark, 0.5);

  &__icon {
    width: 2.8rem;
    height: 2.8rem;
    margin-right: 1.3rem;

    :deep(svg) {
      fill: rgba($c-dark, 0.5);
    }
  }
}

// Notifications panel --------------------------------------------------------
.notifications-panel {
  width: 50rem;
  height: $page-content-min-height;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 0 0 3rem rgba(0, 0, 0, 0.15);

  &__inner-wrap {
    width: 100%;
    height: $page-content-min-height;
    min-height: $page-content-min-height;
    max-height: $page-content-min-height;
    padding-top: 2.2rem;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    position: relative;
  }

  &__panel-header {
    margin-bottom: 2rem;

    &--bigger-bottom-margin {
      margin-bottom: 4.2rem;
    }
  }

  &__list-skeleton {
  }

  &__list-placeholder {
    position: absolute;
    top: 0;
  }

  &__scrollable-block-wrapper {
    flex-grow: 1;
    height: calc(100% - 4rem);
    max-height: calc(100% - 4rem);
  }
  &__scrollable-block {
    height: 100%;
    min-height: 100%;
    max-height: 100%;
    width: calc(100% + 4rem);
    min-width: calc(100% + 4rem);
    position: relative;
    overflow-x: hidden;
    overflow-y: scroll;

    // avoids scrollable block from scrolling more than it's scroll height
    :deep(.generic-scrollable-block__inner) {
      margin-bottom: 0;
    }
  }

  &__sections-list {
    width: 50rem;
    min-width: 50rem;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__notifications-section {
    margin-bottom: 3.2rem;

    &:last-child {
      margin-bottom: 0;
    }

    &--new-ones {
    }
    &--old-ones {
    }
  }
}

@media (max-width: $desktop-width) {
  .notifications-panel {
    height: calc(100vh - 5.6rem);

    &__scrollable-block-wrapper {
      height: calc(100% - 3rem);
      max-height: calc(100% - 3rem);
    }
  }
}

@media (max-width: $tablet-p-width) {
  .notifications-panel {
    height: calc(100vh - 4.9rem);

    &__scrollable-block-wrapper {
      height: calc(100% - 2.3rem);
      max-height: calc(100% - 2.3rem);
    }
  }
}
</style>
